import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { TurningMark } from "./TurningMark";

export type BrandLoaderSize = "sm" | "md" | "lg" | "xl";
export type BrandLoaderVariant = "mark" | "queue";

/**
 * Mark sizes in px: 15 / 24 / 33 / 48. Multiples of 3 keep the mark's rows on
 * whole pixels, so no hairline shows between them at rest.
 */
const MARK_SIZE: Record<BrandLoaderSize, number> = {
  sm: 15,
  md: 24,
  lg: 33,
  xl: 48
};

/** Queue (horizontal) heights: 16 / 24 / 32 / 48 px; width follows the aspect. */
const QUEUE_SIZE_CLASS: Record<BrandLoaderSize, string> = {
  sm: "h-4",
  md: "h-6",
  lg: "h-8",
  xl: "h-12"
};

/** Queue row: 26px squares on a 32px pitch, with one square off-canvas left. */
const QUEUE_RECT_X = [-32, 0, 32, 64, 96];

type BrandLoaderProps = {
  size?: BrandLoaderSize;
  /** `mark` (the turning CaseBlink mark, default) or `queue` (horizontal marching row). */
  variant?: BrandLoaderVariant;
  /** Accessible name announced to screen readers. */
  label?: string;
  className?: string;
};

/**
 * The CaseBlink brand loader. Brand indigo by default; pass a text colour
 * class to recolour, e.g. `className="text-white"` on dark overlays.
 *
 * - `mark` (default): the CaseBlink mark, its rows turning like blocks with a
 *   short hold between turns (`TurningMark`).
 * - `queue`: 26px squares marching to the right in a horizontal row; use when
 *   the loader sits beside a caption (see `LoadingState` `layout="inline"`).
 *
 * Bare indicator only. When the loader needs a caption, use `LoadingState`
 * so the text slot is styled consistently everywhere.
 */
export function BrandLoader({
  size = "lg",
  variant = "mark",
  label = "Loading",
  className
}: BrandLoaderProps) {
  if (variant === "queue") {
    return (
      <svg
        viewBox="0 0 122 26"
        fill="currentColor"
        shapeRendering="crispEdges"
        role="status"
        aria-label={label}
        data-slot="brand-loader"
        className={cn(
          "w-auto shrink-0 text-brand-blue aspect-[61/13]",
          QUEUE_SIZE_CLASS[size],
          className
        )}
      >
        <g className="brand-loader-queue-row">
          {QUEUE_RECT_X.map((x) => (
            <rect key={x} x={x} width="26" height="26" />
          ))}
        </g>
      </svg>
    );
  }

  return (
    <TurningMark
      size={MARK_SIZE[size]}
      turning="loop"
      role="status"
      aria-label={label}
      data-slot="brand-loader"
      className={className}
    />
  );
}

export type LoadingStateLayout = "stack" | "inline";

type LoadingStateProps = {
  /** Caption rendered in the standard text slot next to the loader. */
  text?: ReactNode;
  size?: BrandLoaderSize;
  /** `stack` (default) puts the caption under the loader; `inline` puts it beside. */
  layout?: LoadingStateLayout;
  /** Accessible name for the loader; defaults to `text` when it is a string. */
  label?: string;
  className?: string;
};

/**
 * Loader plus the standard caption slot. This is the only place caption
 * typography for loading states is defined; call sites pass `text` rather
 * than rendering their own label next to a `BrandLoader`.
 */
export function LoadingState({
  text,
  size = "lg",
  layout = "stack",
  label,
  className
}: LoadingStateProps) {
  const resolvedLabel = label ?? (typeof text === "string" ? text : undefined);
  const stacked = layout === "stack";
  return (
    <div
      data-slot="loading-state"
      className={cn(
        "flex items-center justify-center",
        // Inline sets the text size on the wrapper so the queue loader can size
        // itself in `em` (see the `h-[1em]` below) and track the caption.
        stacked ? "flex-col gap-3 text-center" : "flex-row gap-2 text-xs",
        className
      )}
    >
      <BrandLoader
        size={size}
        variant={stacked ? "mark" : "queue"}
        label={resolvedLabel}
        // Inline: match the caption's font size instead of a fixed pixel size.
        className={stacked ? undefined : "h-[1em]"}
      />
      {text ? (
        <p
          data-slot="loading-state-text"
          className={cn(
            "text-muted-foreground",
            stacked ? "text-sm" : "text-xs"
          )}
        >
          {text}
        </p>
      ) : null}
    </div>
  );
}
